import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { appraisalCyclesApi } from '@/api/appraisalCyclesApi';
import { tcRunKeys } from '@/queries/useTcRun';
import type {
  AppraisalCyclesFilterRequest,
  CreateAppraisalCycleRequest,
  UpdateAppraisalCycleRequest,
  CycleAppraisalsFilterRequest,
  UpdateCycleAppraisalRequest,
} from '@/api/types/appraisalCycle.types';

export const appraisalCycleKeys = {
  all: ['appraisalCycles'] as const,
  list: (filter: AppraisalCyclesFilterRequest) => ['appraisalCycles', filter] as const,
  active: ['appraisalCycles', 'active'] as const,
  appraisals: (cycleId: number, filter: CycleAppraisalsFilterRequest) =>
    ['appraisalCycles', cycleId, 'appraisals', filter] as const,
  mappingsPreview: (cycleId: number, page: number, pageSize: number) =>
    ['appraisalCycles', cycleId, 'mappingsPreview', page, pageSize] as const,
};

// Cycle queries

export function useAppraisalCycles(filter: AppraisalCyclesFilterRequest) {
  return useQuery({
    queryKey: appraisalCycleKeys.list(filter),
    queryFn: () => appraisalCyclesApi.getAll(filter),
    placeholderData: (previousData) => previousData,
  });
}

export function useActiveAppraisalCycle() {
  return useQuery({
    queryKey: appraisalCycleKeys.active,
    queryFn: () => appraisalCyclesApi.getActive(),
  });
}

// Cycle mutations

export function useCreateAppraisalCycle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateAppraisalCycleRequest) => appraisalCyclesApi.create(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appraisalCycleKeys.all });
    },
  });
}

export function useUpdateAppraisalCycle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, request, rowVersion }: { id: number; request: UpdateAppraisalCycleRequest; rowVersion?: string }) =>
      appraisalCyclesApi.update(id, request, rowVersion),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appraisalCycleKeys.all });
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        queryClient.invalidateQueries({ queryKey: appraisalCycleKeys.all });
      }
    },
  });
}

export function useDeleteAppraisalCycle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => appraisalCyclesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appraisalCycleKeys.all });
    },
  });
}

export function useImportMappings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cycleId, file }: { cycleId: number; file: File }) =>
      appraisalCyclesApi.importMappings(cycleId, file),
    onSuccess: (_, { cycleId }) => {
      queryClient.invalidateQueries({ queryKey: appraisalCycleKeys.all });
      queryClient.invalidateQueries({ queryKey: ['appraisalCycles', cycleId, 'mappingsPreview'] });
    },
  });
}

export function useStartAppraisalProcess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cycleId, rowVersion }: { cycleId: number; rowVersion?: string }) =>
      appraisalCyclesApi.startProcess(cycleId, rowVersion),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appraisalCycleKeys.all });
    },
  });
}

// Cycle appraisals queries

export function useCycleAppraisals(cycleId: number, filter: CycleAppraisalsFilterRequest, enabled: boolean = true) {
  return useQuery({
    queryKey: appraisalCycleKeys.appraisals(cycleId, filter),
    queryFn: () => appraisalCyclesApi.getAppraisals(cycleId, filter),
    enabled: !!cycleId && enabled,
    placeholderData: (previousData) => previousData,
  });
}

export function useCycleMappingsPreview(cycleId: number, page = 1, pageSize = 50, enabled = false) {
  return useQuery({
    queryKey: appraisalCycleKeys.mappingsPreview(cycleId, page, pageSize),
    queryFn: () => appraisalCyclesApi.getMappingsPreview(cycleId, page, pageSize),
    enabled: !!cycleId && enabled,
    placeholderData: (previousData) => previousData,
  });
}

// Cycle appraisals mutations

export function useUpdateCycleAppraisal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      cycleId,
      appraisalId,
      request,
    }: {
      cycleId: number;
      appraisalId: number;
      request: UpdateCycleAppraisalRequest;
    }) => appraisalCyclesApi.updateAppraisal(cycleId, appraisalId, request),
    onSuccess: (_, { cycleId }) => {
      queryClient.invalidateQueries({ queryKey: ['appraisalCycles', cycleId, 'appraisals'] });
      queryClient.invalidateQueries({ queryKey: ['talentCommittees'] });
      // The admin page edits may change status / rating / name / etc. of an appraisal
      // that is already in a TC Run view; refresh all tcRun caches so a side-by-side
      // TC Run tab reflects the change without a manual reload.
      queryClient.invalidateQueries({ queryKey: tcRunKeys.all });
    },
    onError: (error, { cycleId }) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        queryClient.invalidateQueries({ queryKey: ['appraisalCycles', cycleId, 'appraisals'] });
      }
    },
  });
}

export function useDeleteCycleAppraisal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cycleId, appraisalId }: { cycleId: number; appraisalId: number }) =>
      appraisalCyclesApi.deleteAppraisal(cycleId, appraisalId),
    onSuccess: (_, { cycleId }) => {
      queryClient.invalidateQueries({ queryKey: ['appraisalCycles', cycleId, 'appraisals'] });
      queryClient.invalidateQueries({ queryKey: appraisalCycleKeys.all });
      queryClient.invalidateQueries({ queryKey: ['talentCommittees'] });
      queryClient.invalidateQueries({ queryKey: tcRunKeys.all });
    },
  });
}

export function useBulkDeleteCycleAppraisals() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cycleId, appraisalIds }: { cycleId: number; appraisalIds: number[] }) =>
      appraisalCyclesApi.bulkDeleteAppraisals(cycleId, appraisalIds),
    onSuccess: (_, { cycleId }) => {
      queryClient.invalidateQueries({ queryKey: ['appraisalCycles', cycleId, 'appraisals'] });
      queryClient.invalidateQueries({ queryKey: appraisalCycleKeys.all });
      queryClient.invalidateQueries({ queryKey: ['talentCommittees'] });
      queryClient.invalidateQueries({ queryKey: tcRunKeys.all });
    },
  });
}

export function useSendAppraiseeReminder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cycleId, appraisalId }: { cycleId: number; appraisalId: number }) =>
      appraisalCyclesApi.sendAppraiseeReminder(cycleId, appraisalId),
    onSuccess: (_, { cycleId }) => {
      queryClient.invalidateQueries({ queryKey: ['appraisalCycles', cycleId, 'appraisals'] });
    },
  });
}

export function useSendCollatorReminder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cycleId, appraisalId }: { cycleId: number; appraisalId: number }) =>
      appraisalCyclesApi.sendCollatorReminder(cycleId, appraisalId),
    onSuccess: (_, { cycleId }) => {
      queryClient.invalidateQueries({ queryKey: ['appraisalCycles', cycleId, 'appraisals'] });
    },
  });
}

export function useExportCycleAppraisals() {
  return useMutation({
    mutationFn: ({
      cycleId,
      appraisalIds,
      status,
    }: {
      cycleId: number;
      appraisalIds?: number[];
      status?: string;
    }) => appraisalCyclesApi.exportAppraisals(cycleId, appraisalIds, status),
  });
}

export function useExportAppraiseeList() {
  return useMutation({
    mutationFn: () => appraisalCyclesApi.exportAppraiseeList(),
  });
}
